<template>
  <div class="hero-stats-bar-container">
    <!-- Tier Label -->
    <div class="tier-label" v-if="showTierLabel">
      <i class="fa fa-tachometer"></i>
      <span>Key Metrics</span>
      <span class="tier-hint">Drag to reorder</span>
    </div>

    <draggable
      v-model="orderedStats"
      :animation="200"
      ghost-class="stat-ghost"
      drag-class="stat-drag"
      class="hero-stats-bar"
      :class="{ 'is-dragging': isDragging }"
      @start="isDragging = true"
      @end="handleDragEnd"
    >
      <div
        v-for="stat in orderedStats"
        :key="stat.key"
        class="hero-stat-card"
        :class="[`urgency-${stat.urgency}`, { clickable: stat.clickable }]"
      >
        <!-- Remove Button (top right) -->
        <button
          class="stat-remove-btn"
          title="Hide stat"
          @click.stop="handleRemoveStat(stat.key)"
        >
          <i class="fa fa-times"></i>
        </button>

        <div class="stat-card-content" @click="handleStatClick(stat)">
          <div class="stat-icon-wrapper" :class="`icon-${stat.color || 'default'}`">
            <i :class="stat.icon"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value-row">
              <span class="stat-value">{{ stat.value }}</span>
              <span v-if="stat.trend" class="stat-trend" :class="stat.trend > 0 ? 'trend-up' : 'trend-down'">
                <i :class="stat.trend > 0 ? 'fa fa-arrow-up' : 'fa fa-arrow-down'"></i>
                {{ Math.abs(stat.trend) }}%
              </span>
            </div>
            <div class="stat-label">{{ stat.label }}</div>
            <div v-if="stat.subtitle" class="stat-subtitle">{{ stat.subtitle }}</div>
          </div>
          <!-- Mini sparkline visualization -->
          <div v-if="stat.showSparkline" class="stat-sparkline">
            <svg viewBox="0 0 50 20" preserveAspectRatio="none">
              <polyline
                :points="generateSparklinePoints(stat.sparklineData || [3,5,2,7,4,6,8,5,9,7])"
                fill="none"
                :stroke="getSparklineColor(stat.urgency)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>
    </draggable>
  </div>
</template>

<script>
import draggable from 'vuedraggable';

export default {
  name: 'HeroStatsBar',

  components: {
    draggable
  },

  props: {
    stats: {
      type: Array,
      required: true
      // Each stat: { key, label, value, icon, urgency, trend, color, clickable, showSparkline, sparklineData }
    },
    showTierLabel: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      isDragging: false,
      internalOrder: null
    };
  },

  computed: {
    orderedStats: {
      get() {
        // If we have a saved order, use it
        if (this.internalOrder) {
          return this.internalOrder;
        }
        return [...this.stats];
      },
      set(newOrder) {
        this.internalOrder = newOrder;
      }
    }
  },

  watch: {
    stats: {
      handler(newStats) {
        // Update internal order when stats change, preserving order if possible
        if (this.internalOrder) {
          const newKeys = new Set(newStats.map(s => s.key));
          const orderedKeys = this.internalOrder.map(s => s.key);

          // Keep existing order, update values
          this.internalOrder = orderedKeys
            .filter(key => newKeys.has(key))
            .map(key => newStats.find(s => s.key === key))
            .filter(Boolean);

          // Add any new stats at the end
          const existingKeys = new Set(orderedKeys);
          newStats.forEach(stat => {
            if (!existingKeys.has(stat.key)) {
              this.internalOrder.push(stat);
            }
          });
        }
      },
      deep: true
    }
  },

  methods: {
    handleStatClick(stat) {
      if (stat.clickable) {
        this.$emit('stat-click', stat.key);
      }
    },

    handleDragEnd() {
      this.isDragging = false;
      // Emit the new order
      const newOrder = this.orderedStats.map(s => s.key);
      this.$emit('order-change', newOrder);
    },

    handleRemoveStat(statKey) {
      this.$emit('stat-remove', statKey);
    },

    generateSparklinePoints(data) {
      if (!data || data.length === 0) return '';
      const max = Math.max(...data);
      const min = Math.min(...data);
      const range = max - min || 1;
      const width = 50;
      const height = 20;
      const padding = 2;

      return data.map((val, i) => {
        const x = (i / (data.length - 1)) * width;
        const y = height - padding - ((val - min) / range) * (height - padding * 2);
        return `${x},${y}`;
      }).join(' ');
    },

    getSparklineColor(urgency) {
      const colors = {
        success: '#22c55e',
        warning: '#f59e0b',
        urgent: '#ef4444',
        info: '#3b82f6',
        neutral: '#94a3b8'
      };
      return colors[urgency] || colors.neutral;
    }
  }
};
</script>

<style lang="scss" scoped>
.hero-stats-bar-container {
  margin-bottom: 28px;
}

.tier-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-left: 4px;

  i {
    color: #3abf94;
    font-size: 0.875rem;
  }

  span {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #334155;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .tier-hint {
    font-weight: 400;
    color: #94a3b8;
    text-transform: none;
    letter-spacing: 0;
    font-size: 0.75rem;

    &::before {
      content: '•';
      margin: 0 6px;
    }
  }
}

.hero-stats-bar {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  &.is-dragging {
    .hero-stat-card {
      .stat-remove-btn {
        opacity: 1;
      }
    }
  }
}

.hero-stat-card {
  display: flex;
  align-items: stretch;
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid #e2e8f0;
  position: relative;
  overflow: visible;
  transition: all 0.2s ease;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  &:hover {
    .stat-remove-btn {
      opacity: 1;
    }
  }

  // Remove button (top right - circular like widgets)
  .stat-remove-btn {
    position: absolute;
    top: -8px;
    right: -8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: none;
    background-color: #f1f5f9;
    border-radius: 50%;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.15s ease, background-color 0.15s ease;
    z-index: 5;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    i {
      font-size: 0.625rem;
      color: #64748b;
    }

    &:hover {
      background-color: #fee2e2;
      i {
        color: #dc2626;
      }
    }
  }

  .stat-card-content {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 20px 24px;
    flex: 1;
    min-width: 0;
  }

  &.clickable .stat-card-content {
    cursor: pointer;

    &:hover {
      background-color: #fafbfc;
    }
  }

}

.stat-icon-wrapper {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 1.5rem;
  }

  // Color variants
  &.icon-default {
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
    i { color: #64748b; }
  }

  &.icon-blue {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.05) 100%);
    i { color: #3b82f6; }
  }

  &.icon-green {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(34, 197, 94, 0.05) 100%);
    i { color: #22c55e; }
  }

  &.icon-orange {
    background: linear-gradient(135deg, rgba(249, 115, 22, 0.15) 0%, rgba(249, 115, 22, 0.05) 100%);
    i { color: #f97316; }
  }

  &.icon-red {
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(239, 68, 68, 0.05) 100%);
    i { color: #ef4444; }
  }

  &.icon-purple {
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(139, 92, 246, 0.05) 100%);
    i { color: #8b5cf6; }
  }

  &.icon-teal {
    background: linear-gradient(135deg, rgba(58, 191, 148, 0.15) 0%, rgba(58, 191, 148, 0.05) 100%);
    i { color: #3abf94; }
  }
}

.stat-content {
  flex: 1;
  min-width: 0;
}

.stat-value-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
}

.stat-trend {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;

  i {
    font-size: 0.625rem;
  }

  &.trend-up {
    background-color: rgba(34, 197, 94, 0.1);
    color: #16a34a;
  }

  &.trend-down {
    background-color: rgba(239, 68, 68, 0.1);
    color: #dc2626;
  }
}

.stat-label {
  font-size: 0.9375rem;
  font-weight: 500;
  color: #334155;
}

.stat-subtitle {
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 2px;
}

.stat-sparkline {
  position: absolute;
  right: 16px;
  bottom: 16px;
  width: 60px;
  height: 24px;
  opacity: 0.6;

  svg {
    width: 100%;
    height: 100%;
  }
}

// Draggable ghost and drag styles
.stat-ghost {
  opacity: 0.4;
  background-color: #e2e8f0;
  border: 2px dashed #94a3b8;
}

.stat-drag {
  opacity: 0.95;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  transform: rotate(1deg);
  z-index: 1000;
}
</style>
